import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import AthleteProfile from '../AthleteProfile';
import MobileModal from '../MobileModal';
import AthleteHeadshot from '../AthleteHeadshot';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Coach-scoped roster management for the active team: add/edit/remove athletes,
 * link user accounts, and bulk CSV import. Extracted from the old AthletesTab
 * so it can live inside the Team Management dashboard, filtered to one team.
 */
export default function RosterPanel({ team, onRosterChange }) {
  const { token, isCoach, user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [newAthlete, setNewAthlete] = useState({
    name: '',
    racingclass: '',
    email: '',
    phone: '',
    height: '',
    weight: '',
    ergScore: '',
  });
  const [viewingAthlete, setViewingAthlete] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // CSV Upload state
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [csvUploadStatus, setCsvUploadStatus] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  useEffect(() => {
    if (team) {
      fetchAthletes();
      fetchUsers();
    }
  }, [team]);

  const fetchAthletes = async () => {
    if (!team) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes?team=${encodeURIComponent(team)}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
        onRosterChange?.(data);
      } else {
        console.error('Failed to fetch athletes');
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/athletes/users/club`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const addAthlete = async () => {
    if (!newAthlete.name.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/athletes`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ ...newAthlete, team }),
      });

      if (response.ok) {
        await fetchAthletes();
        setNewAthlete({ name: '', racingclass: '', email: '', phone: '', height: '', weight: '', ergScore: '' });
        setShowAddForm(false);
      } else {
        const error = await response.json();
        flash(`Failed to add athlete: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding athlete:', error);
      flash('Error adding athlete. Please try again.');
    }
  };

  const removeAthlete = async (athlete) => {
    if (!confirm(`Remove ${athlete.name} from the roster?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athlete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        await fetchAthletes();
      } else {
        flash('Failed to remove athlete.');
      }
    } catch (error) {
      console.error('Error removing athlete:', error);
      flash('Error removing athlete. Please try again.');
    }
  };

  const updateAthlete = async (updatedAthlete) => {
    try {
      const payload = { ...updatedAthlete };
      if (Array.isArray(updatedAthlete.teams) && updatedAthlete.teams.length > 0) {
        payload.teams = updatedAthlete.teams;
        payload.team = updatedAthlete.team || updatedAthlete.teams[0];
      }
      const response = await fetch(`${API_URL}/api/athletes/${updatedAthlete.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchAthletes();
        setViewingAthlete(updatedAthlete);
      } else {
        const error = await response.json();
        flash(error.error || 'Failed to update athlete.');
      }
    } catch (error) {
      console.error('Error updating athlete:', error);
      flash('Error updating athlete. Please try again.');
    }
  };

  const linkAthleteToUser = async (athleteId, userId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athleteId}/link-user`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ userId: userId || null }),
      });

      if (response.ok) {
        await fetchAthletes();
        await fetchUsers();
        if (userId) {
          const linkedUser = users.find((u) => u.id === parseInt(userId, 10));
          flash(`Athlete linked to ${linkedUser?.name || 'user account'}`);
        } else {
          flash('Athlete unlinked from user account');
        }
      } else {
        const data = await response.json();
        flash(data.error || 'Failed to link account');
      }
    } catch (error) {
      console.error('Error linking account:', error);
      flash('Error linking account');
    } finally {
      setSaving(false);
    }
  };

  // CSV Upload Functions
  const handleCsvFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvUploadStatus('');
    setCsvPreview([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data.map((row, index) => {
          const errors = [];
          if (!row.Name || row.Name.trim() === '') {
            errors.push('Missing name');
          }
          return {
            row: index + 2,
            name: row.Name?.trim() || '',
            team: row.Team?.trim() || team,
            racingclass: row.RacingClass?.trim() || row.Class?.trim() || '',
            email: row.Email?.trim() || '',
            phone: row.Phone?.trim() || '',
            height: row.Height?.trim() || '',
            weight: row.Weight?.trim() || '',
            ergScore: row.ErgScore?.trim() || row['2k']?.trim() || row['Erg Score']?.trim() || '',
            errors,
            valid: errors.length === 0
          };
        });

        setCsvPreview(rows);
        setShowCsvPreview(true);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        setCsvUploadStatus('Error parsing CSV file. Please check the format.');
      }
    });
  };

  const handleCsvUpload = async () => {
    const validAthletes = csvPreview.filter((a) => a.valid);
    if (validAthletes.length === 0) {
      setCsvUploadStatus('No valid athletes to upload');
      return;
    }

    try {
      setCsvUploadStatus('Uploading...');
      const athletesToUpload = validAthletes.map((a) => ({
        name: a.name,
        team: a.team,
        racingclass: a.racingclass,
        email: a.email,
        phone: a.phone,
        height: a.height,
        weight: a.weight,
        ergScore: a.ergScore,
        updateExisting
      }));

      const response = await fetch(`${API_URL}/api/athletes/bulk-upload`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ athletes: athletesToUpload })
      });

      const data = await response.json();

      if (response.ok) {
        setCsvUploadStatus(
          `✅ Upload complete: ${data.results.created} created, ${data.results.updated} updated, ${data.results.skipped} skipped` +
          (data.results.errors.length > 0 ? ` (${data.results.errors.length} errors)` : '')
        );
        await fetchAthletes();
        setTimeout(() => {
          setShowCsvUpload(false);
          resetCsvUpload();
        }, 3000);
      } else {
        setCsvUploadStatus(`❌ Upload failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      setCsvUploadStatus('❌ Error uploading athletes. Please try again.');
    }
  };

  const resetCsvUpload = () => {
    setCsvPreview([]);
    setShowCsvPreview(false);
    setCsvUploadStatus('');
    setUpdateExisting(false);
  };

  if (viewingAthlete) {
    return (
      <AthleteProfile
        athlete={viewingAthlete}
        onBack={() => {
          setViewingAthlete(null);
          fetchAthletes();
        }}
        onSave={(isCoach() || (user?.athleteId && parseInt(user.athleteId, 10) === parseInt(viewingAthlete.id, 10))) ? updateAthlete : null}
        readOnly={!isCoach() && !(user?.athleteId && parseInt(user.athleteId, 10) === parseInt(viewingAthlete.id, 10))}
      />
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h3 className="text-lg font-semibold">Roster — {team} ({athletes.length})</h3>
        <div className="flex gap-2 flex-col sm:flex-row w-full sm:w-auto">
          <button
            onClick={() => setShowCsvUpload(true)}
            className="btn-theme-secondary px-4 py-2 rounded w-full sm:w-auto flex items-center justify-center gap-2"
          >
            Import CSV
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-theme-primary px-4 py-2 rounded w-full sm:w-auto"
          >
            Add Athlete
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${/fail|error/i.test(message) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {message}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm">Loading roster...</p>
      ) : athletes.length === 0 ? (
        <p className="text-gray-500 text-sm">No athletes on this team yet.</p>
      ) : (
        <div className="space-y-2">
          {athletes.map((athlete) => {
            const linkedUserId = athlete.linked_user_id;
            const linkedUser = linkedUserId ? users.find((u) => u.id === linkedUserId) : null;
            return (
              <div key={athlete.id} className="border rounded-lg p-3 space-y-2 bg-white">
                <div
                  className="flex justify-between items-start cursor-pointer"
                  onClick={() => setViewingAthlete(athlete)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <AthleteHeadshot name={athlete.name} headshotUrl={athlete.headshot_url} size="sm" />
                    <span className={`font-medium ${athlete.is_injured ? 'text-red-700' : ''}`}>{athlete.name}</span>
                    <div className="flex flex-wrap gap-1">
                      {athlete.is_injured && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium" title={athlete.injury_notes || 'Injured'}>
                          Injured
                        </span>
                      )}
                      {(Array.isArray(athlete.teams) && athlete.teams.length > 0
                        ? athlete.teams
                        : (athlete.team ? [athlete.team] : [])
                      ).map((teamName) => (
                        <span key={teamName} className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          {teamName}
                        </span>
                      ))}
                      {linkedUser ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded" title="Linked user account">
                          ✓ Linked to {linkedUser.name}
                        </span>
                      ) : athlete.has_user_account && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                          ⚠ Account exists but not linked
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAthlete(athlete);
                    }}
                    className="text-red-500 hover:bg-red-100 bg-white text-xl p-1 rounded"
                    title="Remove from roster"
                  >
                    ×
                  </button>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <label className="text-xs text-gray-600 whitespace-nowrap">Link Account:</label>
                  <select
                    value={linkedUserId || ''}
                    onChange={(e) => linkAthleteToUser(athlete.id, e.target.value)}
                    className="border p-1 rounded text-xs bg-white flex-1"
                    disabled={saving}
                  >
                    <option value="">No account</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id} disabled={u.athlete_id && u.athlete_id !== athlete.id}>
                        {u.name} ({u.email}) {u.athlete_id && u.athlete_id !== athlete.id ? ' [already linked]' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <MobileModal
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        title={`Add Athlete to ${team}`}
        maxWidth="max-w-lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <input
            type="text"
            placeholder="Name *"
            value={newAthlete.name}
            onChange={(e) => setNewAthlete({ ...newAthlete, name: e.target.value })}
            className="border p-3 rounded text-blue-900 bg-white text-base sm:col-span-2"
            required
          />
          {[
            ['Racing Class (e.g. OW, JLM, BM)', 'racingclass'],
            ['Email', 'email'],
            ['Phone Number', 'phone'],
            ['Height (e.g., 6ft2in)', 'height'],
            ['Weight (e.g., 190 lbs)', 'weight'],
            ['2k Erg Score (e.g., 6:10)', 'ergScore'],
          ].map(([label, field]) => (
            <input
              key={field}
              type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
              placeholder={label}
              value={newAthlete[field]}
              onChange={(e) => setNewAthlete({ ...newAthlete, [field]: e.target.value })}
              className="border p-3 rounded text-blue-900 bg-white text-base"
            />
          ))}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-4">
          <button
            onClick={() => setShowAddForm(false)}
            className="px-4 py-3 text-gray-600 hover:text-red-600 bg-gray-100 rounded text-base"
          >
            Cancel
          </button>
          <button
            onClick={addAthlete}
            className="btn-theme-primary px-4 py-3 rounded text-base"
          >
            Save Athlete
          </button>
        </div>
      </MobileModal>

      <MobileModal
        isOpen={showCsvUpload}
        onClose={() => {
          setShowCsvUpload(false);
          resetCsvUpload();
        }}
        title={`Import Athletes to ${team} from CSV`}
        maxWidth="max-w-4xl"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">CSV Format</h4>
            <p className="text-sm text-blue-700 mb-2">
              Upload a CSV file with the following columns (only <strong>Name</strong> is required). Rows with a
              blank Team column default to {team}.
            </p>
            <code className="text-xs bg-blue-100 px-2 py-1 rounded block overflow-x-auto whitespace-nowrap">
              Name, Team, RacingClass, Email, Phone, Height, Weight, ErgScore
            </code>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Select CSV File</label>
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvFileSelect}
              className="border p-3 rounded w-full text-base bg-white"
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
              className="form-checkbox h-4 w-4 text-theme-primary rounded"
            />
            <span className="text-sm">Update existing athletes if name matches</span>
          </label>

          {showCsvPreview && csvPreview.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">
                  Preview ({csvPreview.filter((a) => a.valid).length} valid / {csvPreview.length} total)
                </h4>
                <button onClick={resetCsvUpload} className="text-sm text-gray-500 hover:text-red-500">
                  Clear
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Row</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left hidden sm:table-cell">Team</th>
                      <th className="px-2 py-1 text-left hidden md:table-cell">Email</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((athlete, idx) => (
                      <tr key={idx} className={athlete.valid ? '' : 'bg-red-50'}>
                        <td className="px-2 py-1 text-gray-500">{athlete.row}</td>
                        <td className="px-2 py-1">{athlete.name || <span className="text-red-500 italic">Missing</span>}</td>
                        <td className="px-2 py-1 hidden sm:table-cell">{athlete.team || '-'}</td>
                        <td className="px-2 py-1 hidden md:table-cell">{athlete.email || '-'}</td>
                        <td className="px-2 py-1">
                          {athlete.valid ? (
                            <span className="text-green-600">✓</span>
                          ) : (
                            <span className="text-red-500 text-xs">{athlete.errors.join(', ')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {csvUploadStatus && (
            <div className={`p-3 rounded ${csvUploadStatus.startsWith('✅') ? 'bg-green-100 text-green-800' : csvUploadStatus.startsWith('❌') ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
              {csvUploadStatus}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <button
              onClick={() => {
                setShowCsvUpload(false);
                resetCsvUpload();
              }}
              className="px-4 py-3 text-gray-600 hover:text-red-600 bg-gray-100 rounded text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleCsvUpload}
              disabled={!csvPreview.some((a) => a.valid) || csvUploadStatus === 'Uploading...'}
              className="btn-theme-primary px-4 py-3 rounded text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {csvUploadStatus === 'Uploading...' ? 'Uploading...' : `Upload ${csvPreview.filter((a) => a.valid).length} Athletes`}
            </button>
          </div>
        </div>
      </MobileModal>
    </div>
  );
}
